import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, Check, HelpCircle } from 'lucide-react';
import { useTutorial } from '../../context/TutorialContext';
import { useExperience } from '../../context/ExperienceContext';

export function TutorialOverlay() {
  const { t } = useTranslation('tutorial');
  const { isManga, isUkiyo } = useExperience();
  const {
    currentTutorial,
    currentStep,
    isTutorialActive,
    nextStep,
    previousStep,
    skipTutorial,
  } = useTutorial();

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const applyRect = useCallback((element: Element) => {
    const rect = element.getBoundingClientRect();
    setTargetRect(rect);
    setHighlightStyle({
      position: 'fixed',
      top: `${rect.top - 8}px`,
      left: `${rect.left - 8}px`,
      width: `${rect.width + 16}px`,
      height: `${rect.height + 16}px`,
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      pointerEvents: 'none',
      zIndex: 9998,
    });
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isTutorialActive && currentTutorial) {
      const step = currentTutorial.steps[currentStep];
      updateHighlight(step.target);
    }
  }, [isTutorialActive, currentStep, currentTutorial]);

  const updateHighlight = (targetSelector: string) => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);

    const element = document.querySelector(targetSelector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      scrollTimerRef.current = setTimeout(() => {
        applyRect(element);
      }, 450);
    } else {
      setTargetRect(null);
    }
  };

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  const computeTooltipPosition = useCallback(() => {
    if (!currentTutorial) return;

    if (!targetRect) {
      setTooltipStyle({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const step = currentTutorial.steps[currentStep];
    const placement = step.placement || 'bottom';
    const gap = 16;
    const margin = 16;
    const tooltipWidth = tooltipRef.current?.offsetWidth || 400;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 250;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top: number;
    let left: number;

    switch (placement) {
      case 'top':
        top = targetRect.top - gap - tooltipHeight;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + gap;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - gap - tooltipWidth;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + gap;
        break;
      default:
        top = targetRect.bottom + gap;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    }

    if (left < margin) left = margin;
    if (left + tooltipWidth > vw - margin) left = vw - margin - tooltipWidth;
    if (top < margin) top = margin;
    if (top + tooltipHeight > vh - margin) top = vh - margin - tooltipHeight;

    setTooltipStyle({ top: `${top}px`, left: `${left}px` });
  }, [currentTutorial, currentStep, targetRect]);

  useEffect(() => {
    computeTooltipPosition();
  }, [computeTooltipPosition]);

  if (!isTutorialActive || !currentTutorial) {
    return null;
  }

  const step = currentTutorial.steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === currentTutorial.steps.length - 1;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-9997" style={{ zIndex: 9997 }} />

      {targetRect && (
        <div
          style={highlightStyle}
          className="border-4 border-blue-500 shadow-lg shadow-blue-500/50"
        />
      )}

      <div
        ref={tooltipRef}
        className={`fixed z-9999 max-w-md w-full animate-fadeIn ${
          isManga
            ? 'bg-white rounded shadow-2xl'
            : isUkiyo
            ? 'bg-[#f9f6f1] rounded-lg shadow-2xl'
            : 'bg-white rounded-lg shadow-2xl'
        }`}
        style={{
          ...tooltipStyle,
          zIndex: 9999,
          ...(isManga ? { border: '3px solid #0a0a0a', borderRadius: 4 } : {}),
          ...(isUkiyo ? { border: '1px solid #d2ccbd' } : {}),
        }}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <HelpCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900" style={isManga ? { fontWeight: 900, letterSpacing: '0.04em' } : isUkiyo ? { fontFamily: "'Noto Serif JP', serif" } : undefined}>{step.title}</h3>
                <p className="text-sm text-gray-500">
                  {t('stepOf', { current: currentStep + 1, total: currentTutorial.steps.length })}
                </p>
              </div>
            </div>
            <button
              onClick={skipTutorial}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-gray-700 mb-4 leading-relaxed">{step.content}</p>

          {step.action && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">{t('tryIt')}:</span> {step.action}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex gap-1">
              {currentTutorial.steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-blue-600'
                      : index < currentStep
                      ? 'w-1.5 bg-blue-400'
                      : 'w-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              {!isFirstStep && (
                <button
                  onClick={previousStep}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common:common.back')}
                </button>
              )}
              <button
                onClick={nextStep}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 font-medium"
              >
                {isLastStep ? (
                  <>
                    {t('common:common.done')}
                    <Check className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    {t('common:common.next')}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
